import React, { useRef, useState } from 'react';
import { Image, StyleSheet } from 'react-native';
import { View } from '@components';
import PropTypes from 'prop-types';

import { Theme } from '@constants';

import { isWeb } from '../../../helpers/Platform';

const randomIdentifier = () => Math.floor(Math.random() * 1000000);

const ImageFormat = {
  png: 'image/png',
  webp: 'image/webp',
};

// Returns a valid image URI from the canvas, or null.
const canvasToImage = async (canvas, imageFormat = ImageFormat.png, encoderOptions = 0.92) => {
  let imageData = null;

  if (isWeb) {
    imageData = canvas.toDataURL(imageFormat, encoderOptions);
  } else {
    imageData = await canvas.toDataURL(imageFormat, encoderOptions);
    imageData = imageData.replace(/^"|"$/g, '');
  }

  const dataCheckString = `data:${imageFormat};base64,`;

  if (imageData != null && imageData !== dataCheckString && imageData.startsWith(dataCheckString)) {
    return imageData;
  }

  return null;
};

// This provides a buffered canvas.  A canvas will be rendered to
// and once drawing is done an image is captured to be displayed.
// This gives the client a chance to draw another frame while
// continuing to display the old frame image.  In steady-state after
// a frame is drawn, an image of the canvas is displayed in the DOM.
// Once setNeedsDisplay() is called, a hidden canvas is added to the
// DOM that can be drawn to while the image continues to be displayed.
// After drawing completes, an image is captured from the canvas and
// the image is shown instead of the canvas.
export const useBufferedCanvas = ({ width, height, draw }) => {
  const identifier = useRef(randomIdentifier()).current;

  const capturedImageData = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const setNeedsDisplay = () => {
    if (isDrawing === false) {
      setIsDrawing(true);
    }
  };

  const components = () => {
    const returnComponents = [];

    if (isDrawing) {
      returnComponents.push(
        <View key={`canvas-${identifier}`} style={styles.canvas}>
          <canvas
            ref={async canvas => {
              if (canvas != null && draw != null) {
                canvas.width = width;
                canvas.height = height;

                const drawSuccessful = draw(canvas);

                if (drawSuccessful) {
                  const imageData = await canvasToImage(canvas);

                  if (imageData != null) {
                    capturedImageData.current = imageData;
                  }
                }

                setIsDrawing(false);
              }
            }}
          />
        </View>,
      );
    }

    if (capturedImageData.current != null) {
      returnComponents.push(
        <Image
          key={`canvas-image-${identifier}`}
          source={{ uri: capturedImageData.current }}
          style={styles.image}
          onError={error => {
            if (error?.nativeEvent != null) {
              console.log(`image error: ${error.nativeEvent.error}`);
            }
          }}
        />,
      );
    }

    return returnComponents;
  };

  return {
    setNeedsDisplay,
    components,
  };
};

useBufferedCanvas.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  // A function to draw to the canvas.  Return true if drawing was successful, or false if the current frame should be aborted.
  draw: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  canvas: {
    bottom: Theme.px(0),
    left: Theme.px(0),
    opacity: 0,
    overflow: 'hidden',
    position: 'absolute',
    right: Theme.px(0),
    top: Theme.px(0),
  },
  image: {
    bottom: Theme.px(0),
    left: Theme.px(0),
    overflow: 'hidden',
    position: 'absolute',
    right: Theme.px(0),
    top: Theme.px(0),
  },
});
